import React, { CSSProperties } from 'react';
import { useAppSelector } from '../../store/hooks';
import CText from './CText';

interface CButtonProps {
  title?: string;
  type?: string;
  color?: string;
  onPress: () => void;
  containerStyle?: CSSProperties;
  style?: CSSProperties;
  icon?: React.ReactNode;
  frontIcon?: React.ReactNode;
  bgColor?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
}

const CButton: React.FC<CButtonProps> = ({
  title,
  type,
  color,
  onPress,
  containerStyle,
  style,
  icon,
  frontIcon,
  bgColor,
  children,
  disabled = false,
  className = '',
  variant = 'primary',
  size = 'md',
  ...props
}) => {
  const colors = useAppSelector((state) => state.theme.theme);

  // Size classes
  const sizeClasses = {
    sm: 'h-10 text-sm rounded-lg',
    md: 'h-12 md:h-14 text-base rounded-xl',
    lg: 'h-14 md:h-16 text-lg rounded-2xl',
  };

  // Variant classes for different button styles
  const getVariantStyles = (): CSSProperties => {
    if (disabled) {
      return {
        backgroundColor: colors.grayScale400,
        boxShadow: 'none',
      };
    }

    switch (variant) {
      case 'secondary':
        return {
          backgroundColor: colors.inputBackground,
          boxShadow: colors.shadowSoft,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          border: `2px solid ${colors.primary}`,
          boxShadow: 'none',
        };
      case 'primary':
      default:
        return {
          backgroundColor: bgColor || colors.primary,
          boxShadow: colors.shadowMedium,
        };
    }
  };

  // Base classes - responsive width for desktop
  const baseClasses = `
    w-full lg:w-auto lg:min-w-[200px] lg:max-w-[400px] lg:px-8
    mt-4 flex items-center justify-center gap-2
    border-none cursor-pointer transition-all duration-200
    ${sizeClasses[size]}
    ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:-translate-y-0.5 active:scale-[0.98]'}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  // Dynamic styles for theme colors
  const dynamicStyle: CSSProperties = {
    ...getVariantStyles(),
    ...containerStyle,
  };

  // Hover style enhancement
  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      e.currentTarget.style.boxShadow = colors.shadowLarge || '';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      e.currentTarget.style.boxShadow = getVariantStyles().boxShadow as string || '';
    }
  };

  return (
    <button
      onClick={!disabled ? onPress : undefined}
      className={baseClasses}
      style={dynamicStyle}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {frontIcon}
      {title && (
        <CText type={type} style={{ fontWeight: 600, ...style }} color={color || (variant === 'outline' ? colors.primary : colors.white)}>
          {title}
        </CText>
      )}
      {icon}
      {children}
    </button>
  );
};

export default CButton;
